import React, { useEffect, useMemo, useRef, useState } from "react";
import { Upload, Shuffle, ListOrdered, Loader2, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { musicApi, uploadTrack } from "./musicApi";
import { TrackRow } from "./TrackRow";

const sortByOrder = (tracks, order) => {
  const idx = new Map((order || []).map((f, i) => [f, i]));
  return [...tracks].sort((a, b) => (idx.has(a.file) ? idx.get(a.file) : 1e6 + a.file.localeCompare(b.file)) - (idx.has(b.file) ? idx.get(b.file) : 1e6));
};

export function LibraryPanel({ themes, zones, station, onChanged }) {
  const [themeId, setThemeId] = useState(themes[0]?.id);
  const [uploads, setUploads] = useState([]);
  const [pending, setPending] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const inputRef = useRef(null);
  const theme = themes.find((t) => t.id === themeId) || themes[0];
  const libTracks = station?.library_tracks?.[theme?.folder] || [];
  const tracks = useMemo(() => sortByOrder(libTracks, theme?.order), [libTracks, theme?.order]);
  const birthday = station?.birthday_track;

  useEffect(() => { musicApi.pendingUploads().then((d) => setPending(d.items || [])).catch(() => {}); }, [station?.seen_at]);

  if (!theme) return null;

  const saveOrder = async (list) => {
    try { await musicApi.patchTheme(theme.id, { order: list.map((t) => t.file) }); onChanged(); } catch { toast.error("Ordine non salvato"); }
  };
  const move = (from, to) => {
    if (to < 0 || to >= tracks.length || from === to) return;
    const list = [...tracks];
    const [it] = list.splice(from, 1);
    list.splice(to, 0, it);
    saveOrder(list);
  };
  const toggleShuffle = async () => {
    await musicApi.patchTheme(theme.id, { shuffle: !(theme.shuffle ?? true) });
    onChanged();
  };
  const onFiles = async (files) => {
    const arr = Array.from(files || []).filter((f) => /\.(mp3|m4a|aac|flac|ogg|wav)$/i.test(f.name));
    if (!arr.length) return toast.error("Scegli file audio (MP3, M4A, FLAC, OGG, WAV)");
    for (const f of arr) {
      setUploads((u) => [...u, { name: f.name, pct: 0 }]);
      try {
        await uploadTrack(theme.id, f, (pct) => setUploads((u) => u.map((x) => (x.name === f.name ? { ...x, pct } : x))));
        toast.success(`${f.name}: caricato, il notebook lo sta scaricando`);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "errore di caricamento"}`);
      }
      setUploads((u) => u.filter((x) => x.name !== f.name));
    }
    musicApi.pendingUploads().then((d) => setPending(d.items || [])).catch(() => {});
    onChanged();
  };
  const act = (p, ok) => p.then(() => { toast.success(ok); onChanged(); }).catch((e) => toast.error(e?.response?.data?.detail || "Errore"));

  return (
    <div className="rounded-2xl bg-white border border-[#e8dcc8] p-4 space-y-3" data-testid="music-library">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-serif text-lg font-bold text-[#2b1410] inline-flex items-center gap-2"><ListOrdered className="h-5 w-5 text-[#C48B31]" /> Libreria brani</h3>
        <span className="text-xs text-[#7c5a4e]">Carica i brani dal telefono o dal Mac, mettili in ordine, suonali subito.</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {themes.map((t) => (
          <button key={t.id} onClick={() => setThemeId(t.id)} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${t.id === theme.id ? "text-white border-transparent" : "bg-white border-[#e8dcc8] text-[#4a1c17] hover:bg-[#faf3ee]"}`} style={t.id === theme.id ? { background: t.color } : {}} data-testid={`music-library-theme-${t.id}`}>
            {t.special ? "🎂 " : ""}{t.name} <span className="opacity-70">· {(station?.library_tracks?.[t.folder] || []).length}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept=".mp3,.m4a,.aac,.flac,.ogg,.wav,audio/*" multiple className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="music-library-file-input" />
        <button onClick={() => inputRef.current?.click()} disabled={!!uploads.length} className="h-10 px-4 rounded-full bg-[#4a1c17] text-white text-sm font-bold inline-flex items-center gap-2 disabled:opacity-60" data-testid="music-library-upload-btn">
          {uploads.length ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica brani in «{theme.name}»
        </button>
        <button onClick={toggleShuffle} className={`h-10 px-3 rounded-full text-sm font-semibold border inline-flex items-center gap-2 ${(theme.shuffle ?? true) ? "bg-[#fff7e6] border-[#f3d9a4] text-[#b8741a]" : "bg-white border-[#e8dcc8] text-[#4a1c17]"}`} data-testid="music-library-shuffle">
          <Shuffle className="h-4 w-4" /> {(theme.shuffle ?? true) ? "Casuale: sì" : "Casuale: no (ordine mio)"}
        </button>
        {!station?.online && <span className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">Notebook non collegato: i brani restano in attesa e arrivano appena si ricollega</span>}
      </div>
      {(uploads.length > 0 || pending.length > 0) && (
        <div className="space-y-1" data-testid="music-library-uploads">
          {uploads.map((u) => (
            <div key={u.name} className="text-xs text-[#4a1c17] flex items-center gap-2"><CloudUpload className="h-3.5 w-3.5" /><span className="truncate flex-1">{u.name}</span><div className="w-32 h-1.5 rounded-full bg-[#f0e6d8] overflow-hidden"><div className="h-full bg-[#C48B31]" style={{ width: `${u.pct}%` }} /></div><span className="tabular-nums w-9 text-right">{u.pct}%</span></div>
          ))}
          {pending.map((p) => (
            <div key={p.id} className="text-xs text-[#7c5a4e] flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /><span className="truncate">{p.filename}</span><span>· in consegna al notebook ({Math.round((p.size || 0) / 1048576)} MB)</span></div>
          ))}
          {pending.length > 0 && (
            <button onClick={() => musicApi.retryUploads().then((d) => toast.success(`Consegna richiesta di nuovo per ${d.requeued} file`)).catch(() => toast.error("Errore"))} className="text-xs font-semibold text-[#4a1c17] underline underline-offset-2" data-testid="music-library-retry-uploads">Il notebook non li scarica? Riprova la consegna</button>
          )}
        </div>
      )}
      {tracks.length === 0 ? (
        <div className="text-sm text-[#7c5a4e] py-6 text-center border border-dashed border-[#e8dcc8] rounded-xl" data-testid="music-library-empty">Nessun brano in «{theme.name}». Carica i primi con il tasto qui sopra.</div>
      ) : (
        <ol className="divide-y divide-[#f5ede2] rounded-xl border border-[#f0e6d8]" data-testid="music-library-list">
          {tracks.map((t, i) => (
            <TrackRow key={t.file} track={t} index={i} total={tracks.length} theme={theme} themes={themes} zones={zones}
              isBirthday={birthday?.folder === theme.folder && birthday?.file === t.file}
              dragging={dragIdx === i}
              onDragStart={() => setDragIdx(i)} onDrop={() => { if (dragIdx != null) move(dragIdx, i); setDragIdx(null); }}
              onMove={(d) => move(i, i + d)}
              onPlay={(zone) => act(musicApi.playTrack(theme.folder, t.file, zone), `«${t.title}» parte ora in ${zones.find((z) => z.id === zone)?.name || zone}`)}
              onMoveTheme={(to) => act(musicApi.moveTrack(theme.folder, t.file, to), `Spostato in ${themes.find((x) => x.folder === to)?.name}`)}
              onDelete={() => window.confirm(`Eliminare «${t.title}» dal notebook?`) && act(musicApi.deleteTrack(theme.folder, t.file), "Brano eliminato")}
              onBirthday={() => act(musicApi.setBirthday(theme.folder, t.file), `«${t.title}» è ora la canzone degli auguri`)} />
          ))}
        </ol>
      )}
    </div>
  );
}
